import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, MapPin, Calendar, Euro, User,
  Building2, CheckCircle, Clock, Send, ExternalLink,
  ChevronRight, RefreshCw, AlertTriangle
} from 'lucide-react';
import {
  getDevisById, getClientById, getIngenieurById, getSocieteById, getAgenceById,
  statutDevisLabels, affaires
} from '../data/mockData';

const DevisDetail = () => {
  const { id } = useParams();
  const devisItem = getDevisById(parseInt(id));

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  if (!devisItem) {
    return (
      <div className="page-container">
        <Link to="/devis" className="btn-back"><ArrowLeft size={18} /> Retour aux devis</Link>
        <div className="empty-state">
          <FileText size={48} />
          <h3>Devis introuvable</h3>
        </div>
      </div>
    );
  }

  const client = getClientById(devisItem.clientId);
  const ingenieur = getIngenieurById(devisItem.ingenieurId);
  const societe = getSocieteById(devisItem.societeId);
  const agence = getAgenceById(devisItem.agenceId);
  const statut = statutDevisLabels[devisItem.statut];

  // Affaire liée si devis accepté
  const affaireLiee = affaires.find(a => a.devisId === devisItem.id);

  return (
    <div className="page-container">
      <Link to="/devis" className="btn-back"><ArrowLeft size={18} /> Retour aux devis</Link>

      {/* Badge TIIME */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.5rem 0.75rem', marginBottom: '1rem',
        background: '#F8FAFC', borderRadius: 'var(--radius)', border: '1px solid var(--gray-200)',
        width: 'fit-content'
      }}>
        <RefreshCw size={14} style={{ color: 'var(--gray-400)' }} />
        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
          Importé depuis TIIME — ID: {devisItem.tiimeId}
        </span>
        {devisItem.dateSyncTiime && (
          <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
            • Sync le {new Date(devisItem.dateSyncTiime).toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>

      {/* En-tête */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)',
        padding: '2rem', marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <span className="offre-reference-large">{devisItem.numero}</span>
              <span className="statut-badge statut-badge-lg" style={{ background: statut.bg, color: statut.color }}>
                {statut.label}
              </span>
              <span className="badge badge-secondary">{devisItem.codeMission}</span>
              <span className="badge" style={{
                background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                color: societe?.id === 1 ? '#1E40AF' : '#9A3412'
              }}>
                {societe?.nom}
              </span>
            </div>
            <h1 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem' }}>{devisItem.designation}</h1>
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <MapPin size={14} /> {devisItem.adresse}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>Montant HT</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>{formatMontant(devisItem.montantHT)}</div>
          </div>
        </div>

        {/* Infos résumées */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--gray-200)' }}>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Client</h4>
            <Link to={`/clients/${client?.id}`} style={{ textDecoration: 'none', color: 'var(--gray-900)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {client?.nom} <ExternalLink size={12} style={{ color: 'var(--primary)' }} />
            </Link>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
              {client?.marchePublic ? 'Marché public' : 'Client privé'}
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Ingénieur en charge</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <div className="user-avatar" style={{ background: ingenieur?.couleur, width: '32px', height: '32px', fontSize: '0.875rem' }}>{ingenieur?.initiales}</div>
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{ingenieur?.prenom} {ingenieur?.nom}</span>
                <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', color: 'var(--gray-500)' }}>{ingenieur?.role}</p>
              </div>
            </div>
            {ingenieur?.email && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--gray-600)' }}>{ingenieur.email}</p>
            )}
          </div>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Agence</h4>
            <p style={{ margin: 0, fontWeight: 600 }}>{agence?.nom || '-'}</p>
          </div>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Dates clés</h4>
            <div style={{ fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Calendar size={12} /> Créé le {new Date(devisItem.dateCreation).toLocaleDateString('fr-FR')}
              </span>
              {devisItem.dateEnvoi && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Send size={12} /> Envoyé le {new Date(devisItem.dateEnvoi).toLocaleDateString('fr-FR')}
                </span>
              )}
              {devisItem.dateValidation && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#059669' }}>
                  <CheckCircle size={12} /> Validé le {new Date(devisItem.dateValidation).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Affaire liée */}
      {affaireLiee && (
        <Link to={`/affaires/${affaireLiee.id}`} style={{ textDecoration: 'none' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '1rem 1.25rem', marginBottom: '1.5rem',
            background: '#F0FDF4', borderRadius: 'var(--radius)', border: '1px solid #BBF7D0',
            cursor: 'pointer', transition: 'all 0.15s'
          }}>
            <CheckCircle size={18} style={{ color: '#16A34A' }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>Affaire créée à partir de ce devis</p>
              <span style={{ fontSize: '0.8125rem', color: '#15803D' }}>{affaireLiee.numero} — {affaireLiee.designation}</span>
            </div>
            <ChevronRight size={18} style={{ color: '#16A34A' }} />
          </div>
        </Link>
      )}

      {/* Lignes du devis */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)',
        overflow: 'hidden', marginBottom: '1.5rem'
      }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Lignes du devis</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="factures-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '50%' }}>Description</th>
                <th style={{ textAlign: 'center' }}>Quantité</th>
                <th style={{ textAlign: 'right' }}>Prix unitaire</th>
                <th style={{ textAlign: 'right' }}>Total HT</th>
              </tr>
            </thead>
            <tbody>
              {devisItem.lignes.map((ligne, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{ligne.description}</td>
                  <td style={{ textAlign: 'center' }}>{ligne.quantite}</td>
                  <td style={{ textAlign: 'right' }}>{formatMontant(ligne.prixUnitaire)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(ligne.quantite * ligne.prixUnitaire)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--gray-300)' }}>
                <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700, fontSize: '1rem' }}>Total HT</td>
                <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.125rem', color: 'var(--primary)' }}>{formatMontant(devisItem.montantHT)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Échéancier */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)',
        overflow: 'hidden', marginBottom: '1.5rem'
      }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Échéancier de paiement</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="factures-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Échéance</th>
                <th style={{ textAlign: 'center' }}>Pourcentage</th>
                <th style={{ textAlign: 'right' }}>Montant HT</th>
              </tr>
            </thead>
            <tbody>
              {devisItem.echeancier.map((ech, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{ech.label}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="badge badge-secondary">{ech.pourcentage}%</span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(ech.montant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Avertissement lecture seule */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        padding: '1rem 1.25rem', background: '#FEF3C7', borderRadius: 'var(--radius)',
        border: '1px solid #FDE68A'
      }}>
        <AlertTriangle size={18} style={{ color: '#D97706', flexShrink: 0, marginTop: '0.125rem' }} />
        <div>
          <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#92400E' }}>Devis en lecture seule</p>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400E' }}>
            Les devis sont gérés et créés dans TIIME. Pour modifier ce devis, rendez-vous directement dans l'application TIIME.
            Les modifications seront automatiquement synchronisées lors de la prochaine synchronisation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DevisDetail;
